import type { JSONSchemaType } from 'ajv';
import type { ReactNode, CSSProperties } from 'react';

import type { FeatureFlags } from '@/lib/feature-flags';

export type JSONSchema<T> = JSONSchemaType<T>;

export interface TextConfig {
  headerText?: string;
  searchPlaceholder?: string;
  attachImageText?: string;
  allDocumentsText?: string;
  loadingSearchText?: string;
  loadingAnswerText?: string;
  errorText?: string;
  followUpPlaceholder?: string;
  positiveAnswerText?: string;
  negativeAnswerText?: string;
  suggestionsTitle?: string;
  uncertaintyFallbackText?: Record<string, string>;
}

export enum ActionType {
  CallPhone = 'call_phone',
  OpenUrl = 'open_url',
  SendEmail = 'send_email',
  RunCallback = 'run_callback',
  LaunchIntercomChat = 'launch_intercom_chat',
  LaunchZendeskChat = 'launch_zendesk_chat',
  LaunchZendeskMessenger = 'launch_zendesk_messenger',
  LaunchFreshdesk = 'launch_freshdesk',
  LaunchSupportTicketForm = 'launch_support_ticket_form',
  LaunchZendeskSunshineChat = 'launch_zendesk_sunshine_chat',
  LaunchWebhookHandoff = 'launch_webhook_handoff',
}

// UI feedback type
export type FeedbackResponse = 'accept' | 'reject';

// API feedback type
export type APIFeedbackResponse = 'positive' | 'negative';

/**
 * Defines a URL redirect rule with pattern matching
 * @example { source: '/knowledge/:id/:path', destination: '/knowledge/test/:path' }
 */
export type RedirectRule = {
  source: string; // Pattern with parameters like '/knowledge/:id/:path'
  destination: string; // Destination with parameter placeholders like '/knowledge/test/:path'
};

export type MarkdownTextBlock = {
  id: string;
  type: 'markdown-text';
  text: string;
};

export type ActionButtonsBlock = {
  id: string;
  type: 'action-buttons';
};

export type ActionInputFormBlock = {
  id: string;
  type: 'action-input-form';
  state: 'ready' | 'processing' | 'completed';
  actionId: string;
  initialValues: ActionInputs;
  schema: ActionInputsSchema;
  onSubmit?: (inputs: ActionInputs) => void;
  inputs: ActionInput[];
};

export type ActionInput = {
  name: string;
  required: boolean;
  schema: {
    type: string;
    title: string;
    description: string;
    oneOf?: {
      const: string;
      title: string;
    }[];
  };
  type: string;
  ui: {
    type: string;
  };
};
export type AnswerBlock = MarkdownTextBlock | ActionButtonsBlock | ActionInputFormBlock;

export type CreateAnswerBlock = Omit<MarkdownTextBlock, 'id'> | Omit<ActionButtonsBlock, 'id'> | Omit<ActionInputFormBlock, 'id'>;

export type AnswerState =
  | 'fetching-search-results'
  | 'fetching-stream'
  | 'streaming'
  | 'getting-action-inputs'
  | 'awaiting-knowledge-group'
  | 'completed';

export type ConversationMessage = {
  question: string;
  answer: string;
};

export type ConversationContext = {
  conversationId?: string;
  messages: ConversationMessage[];
};

export type ActionClick = {
  action: NextBestAction;
  searchQueryId: string;
  query: string;
  answer: string;
  success?: boolean;
};

export type Action = {
  id: string;
  type: ActionType;
  action: {
    version: string;
    options: {
      title?: string;
      [key: string]: any;
    };
  };
};

export interface AnswerAttachment {
  type: AttachmentType;
  url: string;
  filename?: string;
}

export interface Answer {
  id: string;
  state: AnswerState;
  question: string;
  blocks: AnswerBlock[];
  searchResults?: SearchResult[];
  followUpQuestions?: string[];
  searchQueryId?: string;
  feedback?: FeedbackResponse;
  isUncertain?: boolean;
  uncertaintyLanguage?: string | null;
  uncertaintyFallbackText?: string;
  searchIntentId?: string;
  actions?: Action[];
  hasError?: boolean;
  attachments?: AnswerAttachment[];
  statusMessage?: string;
}

export type ActionInputValue = string | number | boolean | null;

export type ActionInputs = Record<string, ActionInputValue>;

export type ActionInputsSchema = JSONSchemaType<ActionInputs>;

export type AnswerStreamRequest =
  | {
      type: 'generate-for-query';
      searchQueryId: string;
      conversationId?: string;
      profileId?: string;
      context?: { type: 'text'; text: string }[];
      attachments?: { type: AttachmentType; url: string; filename?: string }[];
      parameters?: ActionInputs;
    }
  | {
      type: 'invoke-action';
      searchIntentId: string;
      actionId: string;
      parameters: ActionInputs;
      conversationId?: string;
      profileId?: string;
    }
  | {
      type: 'client-execution-result';
      searchIntentId: string;
      actionId: string;
      results: ClientExecutionResult;
      conversationId?: string;
      searchQueryId: string;
      profileId?: string;
    };

export type ClientExecutionResult = {
  success: boolean;
  data?: Record<string, any>;
  reason?: string;
};

export interface SearchResult {
  id: string;
  title: string;
  url: string;
}

export interface SearchResponse {
  results: SearchResult[];
  searchQueryId: string;
  isAgenticResponse?: boolean;
}

export interface FollowUpQuestionsResponse {
  questions: string[];
}

export interface Collection {
  id: string;
  name: string;
  siteEnabled?: boolean;
}

export interface KnowledgeSourceGroup {
  id: string;
  name: string;
  collectionIds: string[];
}

export interface Suggestion {
  id: string;
  question: string;
  conversationId?: string;
  messageCount?: number;
}

export type AttachmentType = 'image' | 'file';

export type AttachedFile = {
  id: string;
  file: File;
  preview: string;
  status: 'uploading' | 'uploaded';
  publicUrl?: string;
  attachmentType: AttachmentType;
};

export interface ChatSearchProps {
  suggestions?: Suggestion[];
  collections?: Array<Collection>;
  selectedCollectionId?: string;
  onCollectionChange?: (collectionId: string) => void;
  selectedRegion?: string;
  onRegionChange?: (region: string) => void;
  apiKey?: string;
  fetchRegionConfig?: (apiKey: string, region: string) => Promise<{ settings?: { collections?: Collection[] } } | null>;
  searchEndpoint: string;
  answerEndpoint: string;
  answerStreamEndpoint: string;
  uploadEndpoint?: string;
  feedbackEndpoint?: string;
  feedbackReasonEndpoint?: string;
  loadConversationEndpoint?: string;
  conversationHistoryEndpoint?: string;
  markConversationReadEndpoint?: string;
  markConversationCompletedEndpoint?: string;
  reconnectHandoff?: (conversationId: string) => void;
  resetHandoff?: () => void;
  followUpQuestionsEndpoint?: string;
  autocompleteEndpoint?: string;
  contentTypeEndpoint?: string;
  disableFollowUpQuestions?: boolean;
  enableFileAttachment?: boolean;
  disableFeedback?: boolean;
  disclaimer?: string;
  offset?: number;
  headers?: Record<string, string>;
  textConfig?: TextConfig;
  searchPath?: string;
  className?: string;
  onToggleSizeButtonClick?: () => void;
  trackEvent?: (eventName: string, data?: Record<string, any>) => void;
  featureFlags?: FeatureFlags;
  onCloseWidget?: () => void;
  isWidgetMode?: boolean;
  nextBestActions?: NextBestAction[];
  bodyActionButtons?: NextBestAction[];
  onNextBestActionClick?: ({
    action,
    searchQueryId,
    query,
    answer,
    conversation,
  }: {
    action: NextBestAction;
    searchQueryId: string;
    query: string;
    answer: string;
    conversation?: ConversationContext;
  }) => void;
  userData?: Record<string, unknown> | null;
  redirectRules?: RedirectRule[];
  secretAttributes?: string;
  allowedRegions?: string[];
  style?: CSSProperties;
  isAgentAssist?: boolean;
  isSearchWidget?: boolean;
  hideCitations?: boolean;
  conversationInlineSlot?: ReactNode;
  liveHumanAgentChatSlot?: ReactNode;
  knowledgeSourceGroups?: KnowledgeSourceGroup[];
}

export interface ChatSearchState {
  showResults: boolean;
  answers: Answer[];
  isLoading: boolean;
  error?: string;
}

type TextChunk = {
  type: 'answer-text-chunk';
  content: string;
};

type GetActionInputs = {
  type: 'get-action-inputs';
  schema: ActionInputsSchema;
  values: Record<string, string | number | boolean | null>;
  searchIntentId: string;
  actionId: string;
  inputs: ActionInput[];
  actionType: string;
  actionKey: string;
};

export type ActionButtons = {
  type: 'action-buttons';
  actions: Array<{
    id: string;
    type: string;
    action: {
      version: string;
      options: {
        title?: string;
        phoneNumber?: string;
        to?: string;
        [key: string]: any;
      };
    };
  }>;
  searchIntentId: string;
};

export type Citations = {
  type: 'citations';
  data: SearchResult[];
};

type AnswerError = {
  type: 'answer-error';
  message: string;
};

export type StatusMessage = {
  type: 'status-message';
  message: string;
};

export type AnswerStreamEvent = TextChunk | GetActionInputs | ActionButtons | Citations | AnswerError | StatusMessage;

type UrlString = string & { __url: never };

const isUrlString = (value: string): value is UrlString => {
  const urlRegExp = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

  return urlRegExp.test(value);
};

export const validateUrlString = (value: string): UrlString => {
  if (!isUrlString(value)) {
    throw new Error(`Invalid URL: ${value}`);
  }

  return value as UrlString;
};

export enum NextBestActionType {
  Link = 'link',
  Email = 'email',
  Phone = 'phone',
  Callback = 'callback',
  Function = 'function',
  ZendeskChat = 'zendesk_chat',
  ZendeskMessenger = 'zendesk_messenger',
  IntercomChat = 'intercom_chat',
  Freshdesk = 'freshdesk',
  ZendeskSunshineChat = 'zendesk_sunshine_chat',
  WebhookHandoff = 'webhook_handoff',
}

export type NextBestActionValue = {
  link: UrlString;
  email: string;
  phone: string;
  callback: string;
  function: string;
  zendesk_chat: string;
  zendesk_messenger: string;
  intercom_chat: string;
  freshdesk: string;
  zendesk_sunshine_chat: string;
  webhook_handoff: string;
};

export type NextBestAction = {
  id?: string;
  label: string;
  type: NextBestActionType;
  icon?: string | UrlString;
  value: NextBestActionValue[NextBestActionType];
};

export type SearchQueryAttribute = string | number | boolean | null;

export type SearchQueryAttributes = Record<string, SearchQueryAttribute>;

export type ConversationSummary = {
  conversationId: string;
  title: string;
  status: 'active' | 'completed';
  subtitle?: string;
  timestamp: string;
  isHandoff?: boolean;
  agentAvatarUrl?: string | null;
  hasUnread?: boolean;
};

export interface ConversationHistoryAPIItem {
  conversationId: string;
  title: string;
  status: 'active' | 'completed';
  lastActivityAt: string;
  createdAt: string;
  isHandoff: boolean;
  messageCount: number;
  agentName?: string | null;
  agentAvatarUrl?: string | null;
  hasUnread?: boolean;
}

export interface ConversationHistoryResponse {
  success: boolean;
  data: ConversationHistoryAPIItem[];
}
